import React from 'react';
import { Link } from 'react-router-dom';

// ScanResults component to display the scan results in a table
const ScanResults = ({ results, statusCode, errorInfo, onBack }) => {
  // Extract properties from the response data, filtering out null values
  const properties = results ? Object.keys(results).filter(key => results[key] !== null) : [];

  // Check for keyExchangeGroup property
  const keyExchangeGroup = results ? results.keyExchangeGroup : null;
  const isQuantumProtected = keyExchangeGroup && keyExchangeGroup.includes("MLKEM");

  // Function to convert camelCase to Title Case
  const camelCaseToTitleCase = (camelCase) => {
    // Add space before capital letters and uppercase the first character
    const withSpaces = camelCase.replace(/([A-Z])/g, ' $1');
    // Capitalize the first letter and trim any leading spaces
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).trim();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Scan Results</h1>

      {/* Status information */}
      <div className="mb-6">
        <p hidden={true} className="text-lg">
          <span  className="font-semibold">Status Code:</span> {statusCode || 'N/A'}
        </p>
        {errorInfo && (
          <p className="text-red-600 mt-2">
            <span className="font-semibold">Error:</span> {errorInfo}
          </p>
        )}
      </div>

      {/* Quantum Protection Status */}
      {keyExchangeGroup && (
        <div className={`p-4 mb-6 rounded-md ${isQuantumProtected ? 'bg-green-100 border border-green-400' : 'bg-yellow-100 border border-yellow-400'}`}>
          <h2 className="text-xl font-bold mb-2">
            {isQuantumProtected ? '🎉 Congratulations!' : '⚠️ Not Yet Protected'}
          </h2>
          <p className="text-lg">
            {isQuantumProtected 
              ? <React.Fragment>Your website is protected against quantum attacks! The encryption uses {keyExchangeGroup}, which provides post-quantum security. PQC can still be implemented in other parts of your organization. <Link to="/contact-us" className="text-blue-600 hover:underline">Contact us</Link> to learn more about comprehensive quantum-safe solutions.</React.Fragment>
              : <React.Fragment>Your website is not yet protected against quantum attacks. The current key exchange group {keyExchangeGroup} does not provide quantum resistance. <Link to="/contact-us" className="text-blue-600 hover:underline">Contact us</Link> for help implementing quantum-safe encryption.</React.Fragment>
            }
          </p>
        </div>
      )}

      {/* Results table */}
      {properties.length > 0 ? (
        <div className="mb-6">
          {/* Desktop/Landscape View - Traditional Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr>
                  {properties.map(prop => (
                    <th key={prop} className="py-2 px-4 border-b border-gray-300 text-left bg-gray-100">
                      {camelCaseToTitleCase(prop)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {properties.map(prop => (
                    <td key={prop} className="py-2 px-4 border-b border-gray-300">
                      {typeof results[prop] === 'object' 
                        ? JSON.stringify(results[prop]) 
                        : String(results[prop])}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile/Portrait View - Flipped Table */}
          <div className="sm:hidden">
            <div className="bg-white border border-gray-300 rounded-md">
              {properties.map(prop => (
                <div key={prop} className="flex border-b border-gray-300 last:border-b-0">
                  <div className="w-1/2 py-2 px-4 bg-gray-100 font-semibold">
                    {camelCaseToTitleCase(prop)}
                  </div>
                  <div className="w-1/2 py-2 px-4 break-words">
                    {typeof results[prop] === 'object' 
                      ? JSON.stringify(results[prop]) 
                      : String(results[prop])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="mb-6">No data available from the scan.</p>
      )}

      {/* Back button */}
      <button
        onClick={onBack}
        className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
      >
        Back to Scanner
      </button>
    </div>
  );
};

export default ScanResults;
